import React, { useState, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
    fetchOrdersToday,
    fetchOrdersFuture,
    fetchOrdersPast,
    markOrderAsPickedUp,
} from "../../util/admin/httpAdmin";
import DashboardTopBarAdmin from "../../components/admin/DashboardTopBarAdmin";
import PrimaryButton from "../../components/shared/PrimaryButton";
import SuccessButton from "../../components/shared/SuccessButton";
import Modal from "../../components/shared/Modal";
import "./OrdersAdmin.css";
import ordersBlueIcon from "../../assets/admin/icons/orders-blue.svg";
import LoadingIndicatorButton from "../../components/shared/LoadingIndicatorButton";


const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("nl-NL", {
        day: "2-digit",
        month: "long",
        year: "numeric",
    });
};

const formatCurrency = (amount) => {
    return new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR' }).format(amount);
};

function OrdersAdmin() {
    const [activeTab, setActiveTab] = useState("today");
    const [filterPickedUp, setFilterPickedUp] = useState(undefined);
    const modalRef = useRef();
    const [selectedOrder, setSelectedOrder] = useState(null);

    const todayOrdersQuery = useQuery({
        queryKey: ["orders", "today"],
        queryFn: fetchOrdersToday,
    });

    const futureOrdersQuery = useQuery({
        queryKey: ["orders", "future"],
        queryFn: fetchOrdersFuture,
    });

    const pastOrdersQuery = useQuery({
        queryKey: ["orders", "past"],
        queryFn: fetchOrdersPast,
    });

    const getOrdersData = () => {
        switch (activeTab) {
            case "today":
                return todayOrdersQuery.data || [];
            case "future":
                return futureOrdersQuery.data || [];
            case "past":
                return pastOrdersQuery.data || [];
            default:
                return [];
        }
    };

    const orders = getOrdersData().filter(order =>
        filterPickedUp !== undefined ? order.markedAsPickedUp === filterPickedUp : true
    );

    const markPickedUpMutation = useMutation({
        mutationFn: markOrderAsPickedUp,
        onSuccess: () => {
            todayOrdersQuery.refetch();
            futureOrdersQuery.refetch();
            pastOrdersQuery.refetch();
            modalRef.current.close();
        },
    });

    const openOrderDetails = (order) => {
        console.log("Opening order details for:", order._id);
        console.log("Order cart items:", order.cartItems);
        
        // Log the selected options for debugging
        order.cartItems.forEach((item, index) => {
            console.log(`Item ${index} selectedOptions:`, item.selectedOptions);
            console.log(`Item ${index} productOptions:`, item.itemId.productOptions);
        });
        
        setSelectedOrder(order);
        modalRef.current.open();
    };

    const isLoading = todayOrdersQuery.isLoading || futureOrdersQuery.isLoading || pastOrdersQuery.isLoading;
    const isError = todayOrdersQuery.isError || futureOrdersQuery.isError || pastOrdersQuery.isError;

    // Add a helper function to render options
    const renderOptionValue = (selection, option, optionIndex) => {
        // If we have a proper option object with type
        if (option && option.type) {
            let displayValue = "";
            
            if (option.type === "select" && typeof selection === 'number') {
                // Single select option
                const choice = option.options[selection];
                if (choice) {
                    const price = choice.price?.$numberDecimal 
                        ? parseFloat(choice.price.$numberDecimal)
                        : parseFloat(choice.price || 0);
                    displayValue = `${choice.name}${price > 0 ? ` (+€${price.toFixed(2)})` : ''}`;
                }
            } else if (option.type === "multiselect") {
                // Handle both array and single number for multiselect
                const selections = Array.isArray(selection) ? selection : [selection];
                const choices = selections.map(idx => {
                    const choice = option.options[idx];
                    if (!choice) return "";
                    
                    const price = choice.price?.$numberDecimal 
                        ? parseFloat(choice.price.$numberDecimal)
                        : parseFloat(choice.price || 0);
                    return `${choice.name}${price > 0 ? ` (+€${price.toFixed(2)})` : ''}`;
                }).filter(Boolean);
                
                displayValue = choices.join(", ");
            } else if (option.type === "text") {
                // Text input option
                displayValue = selection;
            }
            
            return displayValue ? (
                <li key={optionIndex} className="option-item">
                    <span className="option-name">{option.name}:</span>
                    <span className="option-value">{displayValue}</span>
                </li>
            ) : null;
        }
        
        // Fallback for direct option objects (added manually to DB)
        if (typeof selection === 'object' && selection.name) {
            return (
                <li key={optionIndex} className="option-item">
                    <span className="option-name">Option:</span>
                    <span className="option-value">
                        {selection.name}
                        {selection.price ? ` (+€${parseFloat(selection.price).toFixed(2)})` : ''}
                    </span>
                </li>
            );
        }
        
        // Fallback for simple values
        if (selection !== undefined && selection !== null) {
            return (
                <li key={optionIndex} className="option-item">
                    <span className="option-name">Option {optionIndex}:</span>
                    <span className="option-value">
                        {typeof selection === 'object' ? JSON.stringify(selection) : selection.toString()}
                    </span>
                </li>
            );
        }
        
        return null;
    };

    return (
        <div>
            <DashboardTopBarAdmin
                title="Bestellingen"
                icon={ordersBlueIcon}
                description="Beheer hier de bestellingen van jouw restaurant"
            />

            <div className="orders-container py-20 px-20">
                <div className="orders-header">
                    <div className="tabs-container">
                        <div className="order-tabs">
                            <button 
                                className={`tab-button ${activeTab === "today" ? "active" : ""}`} 
                                onClick={() => setActiveTab("today")}
                            >
                                Vandaag Opgehaald
                            </button>
                            <button 
                                className={`tab-button ${activeTab === "future" ? "active" : ""}`} 
                                onClick={() => setActiveTab("future")}
                            >
                                Vanaf morgen opgehaald
                            </button>
                            <button 
                                className={`tab-button ${activeTab === "past" ? "active" : ""}`} 
                                onClick={() => setActiveTab("past")}
                            >
                                Verleden
                            </button>
                        </div>
                    </div>
                    
                    <div className="filter-container">
                        <label className="filter-label">Filter op opgehaald:</label>
                        <select
                            className="filter-select"
                            value={filterPickedUp === undefined ? "undefined" : filterPickedUp.toString()}
                            onChange={(e) => setFilterPickedUp(e.target.value === "undefined" ? undefined : e.target.value === "true")}
                        >
                            <option value="undefined">Alles</option>
                            <option value="true">Opgehaald</option>
                            <option value="false">Niet opgehaald</option>
                        </select>
                    </div>
                </div>

                <div className="orders-content">
                    {isLoading && (
                        <div className="loading-indicator">
                            <LoadingIndicatorButton />
                        </div>
                    )}
                    
                    {isError && (
                        <div className="error-message">
                            <p className="error-text">Er is een fout opgetreden bij het ophalen van de bestellingen.</p>
                        </div>
                    )}
                    
                    {!isLoading && !isError && orders.length === 0 && (
                        <div className="empty-state">
                            <p>Geen bestellingen gevonden.</p>
                        </div>
                    )}
                    
                    {!isLoading && !isError && orders.length > 0 && (
                        <div className="orders-grid">
                            {orders.map((order) => (
                                <div key={order._id} className={`order-card ${order.markedAsPickedUp ? 'picked-up' : ''}`}>
                                    <div className="order-card-header">
                                        <h3 className="order-customer">{order.customerDetails.name}</h3>
                                        <div className="order-status">
                                            {order.markedAsPickedUp ? (
                                                <span className="status-badge picked-up">Opgehaald</span>
                                            ) : (
                                                <span className="status-badge pending">Nog op te halen</span>
                                            )}
                                        </div>
                                    </div>
                                    
                                    <div className="order-card-content">
                                        <div className="order-detail">
                                            <span className="detail-label">Ophalen op:</span>
                                            <span className="detail-value">{formatDate(order.date)}</span>
                                        </div>
                                        <div className="order-detail">
                                            <span className="detail-label">Tijdslot:</span>
                                            <span className="detail-value">
                                                {typeof order.timeSlot === 'object' 
                                                    ? `${order.timeSlot.start} - ${order.timeSlot.end}`
                                                    : order.timeSlot}
                                            </span>
                                        </div>
                                        <div className="order-detail">
                                            <span className="detail-label">Bedrag:</span>
                                            <span className="detail-value">{formatCurrency(order.totalAmount)}</span>
                                        </div>
                                    </div>
                                    
                                    <div className="order-card-footer">
                                        <SuccessButton onClick={() => openOrderDetails(order)}>Bekijk details</SuccessButton>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <Modal ref={modalRef}>
                    {selectedOrder && (
                        <div className="modal-content">
                            <div className="modal-header">
                                <h3>Bestelling van {selectedOrder.customerDetails.name}</h3>
                                <div className="modal-status">
                                    {selectedOrder.markedAsPickedUp ? (
                                        <span className="status-badge picked-up">Opgehaald</span>
                                    ) : (
                                        <span className="status-badge pending">Nog op te halen</span>
                                    )}
                                </div>
                            </div>

                            <div className="modal-details">
                                <div className="details-section">
                                    <h4>Klantgegevens</h4>
                                    <div className="customer-details">
                                        <div className="detail-item">
                                            <span className="detail-label">Naam:</span>
                                            <span className="detail-value">{selectedOrder.customerDetails.name}</span>
                                        </div>
                                        <div className="detail-item">
                                            <span className="detail-label">Telefoon:</span>
                                            <span className="detail-value">{selectedOrder.customerDetails.phone}</span>
                                        </div>
                                        <div className="detail-item">
                                            <span className="detail-label">Email:</span>
                                            <span className="detail-value">{selectedOrder.customerDetails.email}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="details-section">
                                    <h4>Ophaalgegevens</h4>
                                    <div className="pickup-details">
                                        <div className="detail-item">
                                            <span className="detail-label">Datum:</span>
                                            <span className="detail-value">{formatDate(selectedOrder.date)}</span>
                                        </div>
                                        <div className="detail-item">
                                            <span className="detail-label">Tijdslot:</span>
                                            <span className="detail-value">
                                                {typeof selectedOrder.timeSlot === 'object' 
                                                    ? `${selectedOrder.timeSlot.start} - ${selectedOrder.timeSlot.end}`
                                                    : selectedOrder.timeSlot}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="details-section">
                                    <h4>Producten</h4>
                                    <div className="products-list">
                                        {selectedOrder.cartItems.map((item, index) => {
                                            console.log(`Order item ${index}:`, item);
                                            console.log(`Selected options:`, item.selectedOptions);
                                            console.log(`Product options:`, item.itemId.productOptions);
                                            
                                            return (
                                            <div key={index} className="product-item">
                                                <span className="product-name">{item.itemId.name}</span>
                                                <span className="product-quantity">×{item.quantity}</span>
                                                
                                                {/* Display selected options if they exist */}
                                                {item.selectedOptions && Object.keys(item.selectedOptions).length > 0 && (
                                                    <ul className="product-options">
                                                        {Object.entries(item.selectedOptions).map(([optionIndex, selection]) => {
                                                            // Get the option directly from the array using the index
                                                            const productOptions = item.itemId.productOptions || [];
                                                            const option = productOptions[parseInt(optionIndex)];
                                                            
                                                            if (!option) {
                                                                console.warn(`Option with index ${optionIndex} not found in product options`);
                                                                return null;
                                                            }
                                                            
                                                            return renderOptionValue(selection, option, optionIndex);
                                                        })}
                                                    </ul>
                                                )}
                                            </div>
                                        )})}
                                        <div className="order-total">
                                            <span className="total-label">Totaalbedrag:</span>
                                            <span className="total-value">{formatCurrency(selectedOrder.totalAmount)}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="modal-actions">
                                {!selectedOrder.markedAsPickedUp && (
                                    <SuccessButton 
                                        onClick={() => markPickedUpMutation.mutate(selectedOrder._id)} 
                                        disabled={markPickedUpMutation.isLoading}
                                        className="pickup-button"
                                    >
                                        {markPickedUpMutation.isLoading ? (
                                            <LoadingIndicatorButton />
                                        ) : (
                                            "Markeer als opgehaald"
                                        )}
                                    </SuccessButton>
                                )}
                                <PrimaryButton onClick={() => modalRef.current.close()} className="close-button">
                                    Sluiten
                                </PrimaryButton>
                            </div>
                        </div>
                    )}
                </Modal>
            </div>
        </div>
    );
}

export default OrdersAdmin;
